import { useCallback, useId } from "react";
import { ArrowUp, ArrowDown, Trash2 } from "lucide-react";

export default function PhotoPicker({ value = [], onChange }) {
  const uid = useId();
  const inputId = `product-photo-input-${uid}`;  
  const addFiles = useCallback(
    (files) => {
      const next = [...value, ...Array.from(files).map((f) => ({ file: f, photo_url: null }))];
      onChange(next.slice(0, 5)); // cap at 5
    },
    [value, onChange]
  );

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const move = (from, to) => {
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next.slice(0, 5));
  };

  return (
    <div>
      <label className="text-sm font-medium text-gray-800 mb-1 block">Photos</label>

      <div
        className="border-dashed border-2 rounded-2xl p-6 text-center text-gray-600 hover:bg-gray-50 transition"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          addFiles(e.dataTransfer.files);
        }}
      >
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => addFiles(e.target.files)}
          className="hidden"
          id={inputId}
        />
        <label htmlFor={inputId} className="cursor-pointer font-medium">
          Drag & drop or{" "}
          <span className="text-indigo-600 underline underline-offset-4 decoration-indigo-300">browse</span>
        </label>
        <p className="text-xs text-gray-500 mt-1">Up to 5 images</p>
      </div>

      {!!value.length && (
        <ul className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {value.slice(0, 5).map((p, i) => {
            const src = p.photo_url || (p.file ? URL.createObjectURL(p.file) : "");
            return (
              <li key={i} className="relative border rounded-xl overflow-hidden group">
                {src ? (
                  <img src={src} alt="" className="h-28 w-full object-cover transition group-hover:scale-[1.01]" />
                ) : (
                  <div className="h-28 bg-gray-50" />
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent text-white text-[11px] p-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="space-x-1">
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={() => move(i, i - 1)}
                        className="disabled:opacity-40 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/15 hover:bg-white/25"
                        title="Move up"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                        Up
                      </button>
                      <button
                        type="button"
                        disabled={i === value.length - 1}
                        onClick={() => move(i, i + 1)}
                        className="disabled:opacity-40 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/15 hover:bg-white/25"
                        title="Move down"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                        Down
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/80 hover:bg-red-500 text-white"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
